/**
 * PERSONAL ACTIVITY EXPORT
 *
 * Client-side CSV / XLS / PDF generation for the Personal Activity Report.
 * No server-side render cost. Reads only what the user already loaded.
 *
 * Touches no audit/financial tables.
 */

import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

export interface PersonalActivityRow {
  id: string
  event_category: string
  event_action: string
  target_type?: string | null
  target_id?: string | null
  target_label?: string | null
  search_query?: string | null
  search_surface?: string | null
  route_path?: string | null
  tab_id?: string | null
  details?: Record<string, unknown> | null
  ip_address?: string | null
  user_agent?: string | null
  session_id?: string | null
  created_at: string
}

const HEADERS: Array<[keyof PersonalActivityRow, string]> = [
  ['created_at', 'Timestamp (UTC)'],
  ['event_category', 'Category'],
  ['event_action', 'Action'],
  ['target_type', 'Target Type'],
  ['target_id', 'Target ID'],
  ['target_label', 'Target Label'],
  ['search_surface', 'Search Surface'],
  ['search_query', 'Search Query'],
  ['route_path', 'Route'],
  ['tab_id', 'Tab']
]

function fmtCell(row: PersonalActivityRow, key: keyof PersonalActivityRow): string {
  const v = row[key]
  if (v === null || v === undefined) return ''
  if (typeof v === 'object') {
    try { return JSON.stringify(v) } catch { return '' }
  }
  return String(v)
}

function csvEscape(v: string): string {
  if (v == null) return ''
  const needsQuote = /[",\n\r]/.test(v)
  const escaped = v.replace(/"/g, '""')
  return needsQuote ? `"${escaped}"` : escaped
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function defaultFilename(prefix: string, ext: string, adminEmail?: string): string {
  const ts = new Date().toISOString().replace(/[:.]/g, '-')
  const who = adminEmail ? adminEmail.replace(/[^a-z0-9_-]+/gi, '_') : 'admin'
  return `${prefix}_${who}_${ts}.${ext}`
}

export function exportPersonalActivityCsv(rows: PersonalActivityRow[], adminEmail?: string): void {
  const headerLine = HEADERS.map(([, label]) => csvEscape(label)).join(',')
  const lines = rows.map(r =>
    HEADERS.map(([key]) => csvEscape(fmtCell(r, key))).join(',')
  )
  const body = [headerLine, ...lines].join('\r\n')
  const blob = new Blob(['\uFEFF' + body], { type: 'text/csv;charset=utf-8' })
  downloadBlob(blob, defaultFilename('personal_activity', 'csv', adminEmail))
}

export function exportPersonalActivityXls(rows: PersonalActivityRow[], adminEmail?: string): void {
  const aoa: unknown[][] = [
    HEADERS.map(([, label]) => label),
    ...rows.map(r => HEADERS.map(([key]) => fmtCell(r, key)))
  ]
  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = [
    { wch: 22 }, { wch: 12 }, { wch: 22 }, { wch: 14 }, { wch: 26 },
    { wch: 32 }, { wch: 16 }, { wch: 28 }, { wch: 22 }, { wch: 18 }
  ]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Activity')
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  downloadBlob(blob, defaultFilename('personal_activity', 'xlsx', adminEmail))
}

export function exportPersonalActivityPdf(
  rows: PersonalActivityRow[],
  adminEmail?: string,
  meta?: { from?: string; to?: string }
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
  const title = 'Aureus — Personal Activity Report'
  const subtitle = [
    adminEmail ? `Admin: ${adminEmail}` : null,
    meta?.from ? `From: ${meta.from}` : null,
    meta?.to ? `To: ${meta.to}` : null,
    `Rows: ${rows.length}`
  ].filter(Boolean).join('   ')

  doc.setFontSize(14)
  doc.text(title, 40, 36)
  doc.setFontSize(9)
  doc.text(subtitle, 40, 52)

  autoTable(doc, {
    startY: 64,
    head: [HEADERS.map(([, label]) => label)],
    body: rows.map(r => HEADERS.map(([key]) => fmtCell(r, key))),
    styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
    headStyles: { fillColor: [33, 33, 33], textColor: 255 },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    columnStyles: {
      0: { cellWidth: 110 },
      1: { cellWidth: 60 },
      2: { cellWidth: 110 },
      3: { cellWidth: 70 },
      4: { cellWidth: 110 },
      5: { cellWidth: 140 },
      6: { cellWidth: 70 },
      7: { cellWidth: 140 },
      8: { cellWidth: 90 },
      9: { cellWidth: 70 }
    }
  })

  doc.save(defaultFilename('personal_activity', 'pdf', adminEmail))
}
